import { useEffect, useRef } from "react";
import {
  FiCopy,
  FiEdit,
  FiEye,
  FiEyeOff,
  FiFolderPlus,
  FiLink,
  FiLock,
  FiMove,
  FiTrash2,
  FiUnlock,
} from "react-icons/fi";

type ItemType = "folder" | "media";

interface ContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  itemType: ItemType;
  item?:
    | {
        id: string;
        accessLevel?: string | undefined;
      }
    | undefined;
  onAction: (action: string) => void;
  onClose: () => void;
}

interface MenuAction {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  danger?: boolean | undefined;
  disabled?: boolean | undefined;
}

export function ContextMenu({
  isOpen,
  position,
  itemType,
  item,
  onAction,
  onClose,
}: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isPrivate = item?.accessLevel === "private";

  // Define actions based on item type
  const folderActions: MenuAction[] = [
    { id: "new-subfolder", label: "New Subfolder", icon: FiFolderPlus },
    { id: "rename", label: "Rename", icon: FiEdit },
    { id: "delete", label: "Delete", icon: FiTrash2, danger: true },
  ];

  const mediaActions: MenuAction[] = [
    { id: "view", label: "View", icon: FiEye },
    { id: "rename", label: "Rename", icon: FiEdit },
    { id: "move", label: "Move", icon: FiMove },
    { id: "duplicate", label: "Duplicate", icon: FiCopy },
    { id: "link", label: "Link Media", icon: FiLink },
    {
      id: "toggle-visibility",
      label: isPrivate ? "Make Public" : "Make Private",
      icon: isPrivate ? FiUnlock : FiLock,
    },
    {
      id: "delete",
      label: "Delete",
      icon: FiTrash2,
      danger: true,
    },
  ];

  const actions = itemType === "folder" ? folderActions : mediaActions;

  const handleAction = (actionId: string, disabled?: boolean) => {
    if (disabled) return;
    onAction(actionId);
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[200px] rounded-lg border bg-white py-1 shadow-lg"
      style={{
        top: `${position.y}px`,
        left: `${position.x}px`,
      }}
      role="menu"
      aria-label="Context menu"
    >
      {actions.map((action) => {
        const Icon = action.icon;
        return (
          <button
            key={action.id}
            type="button"
            onClick={() => handleAction(action.id, action.disabled)}
            disabled={action.disabled}
            className={`${
              action.danger ? "text-red-600 hover:bg-red-50" : "text-gray-700 hover:bg-gray-100"
            } ${
              action.disabled ? "cursor-not-allowed opacity-50" : ""
            } flex w-full items-center gap-3 px-4 py-2 text-left text-sm`}
            role="menuitem"
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {action.label}
          </button>
        );
      })}
    </div>
  );
}
